import { useState } from 'react';
import type { Booking, BookingStatus, Payment, PayMethod } from '../../../mocks/bookings';
import { statusConfig, payStatusConfig } from './BookingTable';
import { agencyInfo } from '../../../mocks/dashboard';

const brand = agencyInfo.brandColor;

const allStatuses: BookingStatus[] = ['Confirmed', 'Pending', 'On Hold', 'Cancelled', 'Completed'];
const payMethods: PayMethod[] = ['Bank Transfer', 'Credit Card', 'Cash', 'Cheque'];

interface BookingDetailModalProps {
  booking: Booking;
  onClose: () => void;
  onStatusChange: (id: string, status: BookingStatus) => void;
}

export default function BookingDetailModal({ booking, onClose, onStatusChange }: BookingDetailModalProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'supplier' | 'payments' | 'notes'>('overview');
  const [status, setStatus] = useState<BookingStatus>(booking.status);
  const [payments, setPayments] = useState<Payment[]>(booking.payments);
  const [showPayForm, setShowPayForm] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState<PayMethod>('Bank Transfer');
  const [payRef, setPayRef] = useState('');
  const [payNote, setPayNote] = useState('');

  const sc = statusConfig[status];
  const totalPaid = payments.reduce((s, p) => s + p.amount, 0);
  const balance = booking.totalAmount - totalPaid;
  const margin = booking.supplier.sellPrice - booking.supplier.costPrice;
  const marginPct = Math.round((margin / booking.supplier.sellPrice) * 100);

  const handleStatusChange = (s: BookingStatus) => {
    setStatus(s);
    onStatusChange(booking.id, s);
  };

  const handleAddPayment = () => {
    if (!payAmount || isNaN(Number(payAmount))) return;
    const newPay: Payment = {
      id: `P-${Date.now()}`,
      date: new Date().toISOString().slice(0, 10),
      amount: Number(payAmount),
      method: payMethod,
      reference: payRef || `REF-${Date.now()}`,
      notes: payNote,
      recordedBy: 'Mohammed Khalid',
    };
    setPayments((p) => [...p, newPay]);
    setPayAmount('');
    setPayRef('');
    setPayNote('');
    setShowPayForm(false);
  };

  const tabs = [
    { id: 'overview' as const, label: 'Overview', icon: 'ri-information-line' },
    { id: 'supplier' as const, label: 'Supplier', icon: 'ri-store-2-line' },
    { id: 'payments' as const, label: 'Payments', icon: 'ri-money-dollar-circle-line' },
    { id: 'notes' as const, label: 'Notes', icon: 'ri-sticky-note-line' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-2xl h-full bg-white shadow-2xl flex flex-col overflow-hidden">

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${brand}15` }}
          >
            <i className={`${booking.serviceIcon} text-lg`} style={{ color: brand }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base font-bold text-gray-800">{booking.id}</h2>
              <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${sc.bg} ${sc.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                {status}
              </span>
            </div>
            <p className="text-xs text-gray-500 truncate mt-0.5">{booking.service}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
              <i className="ri-printer-line" /> Print
            </button>
            <button className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
              <i className="ri-mail-send-line" /> Email
            </button>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>

        {/* Customer + Status Bar */}
        <div className="px-6 py-3 bg-gray-50/60 border-b border-gray-100">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: brand }}>
                {booking.customerAvatar}
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">{booking.customer}</p>
                <p className="text-xs text-gray-400">{booking.customerPhone} · {booking.customerEmail}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              {allStatuses.map((s) => {
                const c = statusConfig[s];
                return (
                  <button
                    key={s}
                    onClick={() => handleStatusChange(s)}
                    className="text-xs font-semibold px-2.5 py-1 rounded-full cursor-pointer transition-all whitespace-nowrap"
                    style={
                      status === s
                        ? { backgroundColor: brand, color: 'white' }
                        : { backgroundColor: 'white', color: '#6B7280', border: '1px solid #E5E7EB' }
                    }
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="px-6 py-4 grid grid-cols-4 gap-3 border-b border-gray-100">
          {[
            { label: 'Total', value: `${booking.currency} ${booking.totalAmount.toLocaleString()}`, sub: `${booking.pax} pax`, icon: 'ri-money-dollar-circle-line', color: brand },
            { label: 'Paid', value: `${booking.currency} ${totalPaid.toLocaleString()}`, sub: `${payments.length} payment(s)`, icon: 'ri-checkbox-circle-line', color: '#059669' },
            { label: 'Balance', value: balance <= 0 ? 'Settled' : `${booking.currency} ${balance.toLocaleString()}`, sub: balance > 0 ? 'Outstanding' : 'Fully paid', icon: 'ri-time-line', color: balance > 0 ? '#DC2626' : '#059669' },
            { label: 'Margin', value: `${marginPct}%`, sub: `${booking.currency} ${margin.toLocaleString()}`, icon: 'ri-percent-line', color: '#D97706' },
          ].map((s) => (
            <div key={s.label} className="bg-gray-50 rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <i className={`${s.icon} text-xs`} style={{ color: s.color }} />
                <p className="text-xs text-gray-400">{s.label}</p>
              </div>
              <p className="text-sm font-bold" style={{ color: s.color }}>{s.value}</p>
              <p className="text-xs text-gray-400">{s.sub}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 px-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-1.5 px-1 py-3 mr-5 text-sm font-medium cursor-pointer whitespace-nowrap"
              style={activeTab === tab.id ? { color: brand, borderBottom: `2px solid ${brand}` } : { color: '#9CA3AF' }}
            >
              <i className={`${tab.icon} text-sm`} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* Overview */}
          {activeTab === 'overview' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Service Type', value: booking.serviceType, icon: 'ri-suitcase-line' },
                  { label: 'Destination', value: booking.destination, icon: 'ri-map-pin-line' },
                  { label: 'Travel Date', value: booking.travelDate, icon: 'ri-calendar-line' },
                  { label: 'Return Date', value: booking.returnDate, icon: 'ri-calendar-check-line' },
                  { label: 'Duration', value: `${booking.nights} nights`, icon: 'ri-moon-line' },
                  { label: 'Passengers', value: `${booking.pax} pax`, icon: 'ri-user-3-line' },
                  { label: 'Agent', value: booking.agent, icon: 'ri-user-settings-line' },
                  { label: 'Created', value: booking.createdAt, icon: 'ri-time-line' },
                ].map((f) => (
                  <div key={f.label} className="bg-gray-50 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <i className={`${f.icon} text-xs text-gray-400`} />
                      <p className="text-xs text-gray-400">{f.label}</p>
                    </div>
                    <p className="text-sm font-semibold text-gray-700">{f.value}</p>
                  </div>
                ))}
              </div>
              {booking.notes && (
                <div className="bg-amber-50 rounded-xl px-4 py-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <i className="ri-sticky-note-line text-xs text-amber-500" />
                    <p className="text-xs font-semibold text-amber-600">Notes</p>
                  </div>
                  <p className="text-sm text-amber-800 leading-relaxed">{booking.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Supplier */}
          {activeTab === 'supplier' && (
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-2xl">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${brand}15` }}>
                  <i className="ri-store-2-line text-xl" style={{ color: brand }} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-800">{booking.supplier.name}</h3>
                  <p className="text-sm text-gray-500">{booking.supplier.type}</p>
                  <p className="text-xs font-semibold mt-1" style={{ color: brand }}>Ref: {booking.supplier.reference}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Contact Person', value: booking.supplier.contactPerson, icon: 'ri-user-line' },
                  { label: 'Phone', value: booking.supplier.phone, icon: 'ri-phone-line' },
                  { label: 'Email', value: booking.supplier.email, icon: 'ri-mail-line' },
                  { label: 'Reference', value: booking.supplier.reference, icon: 'ri-hashtag' },
                ].map((f) => (
                  <div key={f.label} className="bg-gray-50 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <i className={`${f.icon} text-xs text-gray-400`} />
                      <p className="text-xs text-gray-400">{f.label}</p>
                    </div>
                    <p className="text-sm font-semibold text-gray-700 break-all">{f.value}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Cost Price', value: `${booking.currency} ${booking.supplier.costPrice.toLocaleString()}`, color: '#DC2626', icon: 'ri-arrow-down-line' },
                  { label: 'Sell Price', value: `${booking.currency} ${booking.supplier.sellPrice.toLocaleString()}`, color: brand, icon: 'ri-arrow-up-line' },
                  { label: 'Profit Margin', value: `${booking.currency} ${margin.toLocaleString()} (${marginPct}%)`, color: '#059669', icon: 'ri-percent-line' },
                ].map((f) => (
                  <div key={f.label} className="rounded-xl px-4 py-3" style={{ backgroundColor: `${f.color}08` }}>
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <i className={`${f.icon} text-xs`} style={{ color: f.color }} />
                      <p className="text-xs" style={{ color: f.color }}>{f.label}</p>
                    </div>
                    <p className="text-sm font-bold" style={{ color: f.color }}>{f.value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payments */}
          {activeTab === 'payments' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm font-semibold text-gray-700">Payment History</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {totalPaid > 0 ? `${booking.currency} ${totalPaid.toLocaleString()} collected` : 'No payments yet'}
                    {balance > 0 && ` · ${booking.currency} ${balance.toLocaleString()} outstanding`}
                  </p>
                </div>
                <button
                  onClick={() => setShowPayForm((v) => !v)}
                  className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-xl text-white cursor-pointer hover:opacity-90 whitespace-nowrap"
                  style={{ backgroundColor: brand }}
                >
                  <i className="ri-add-line" />
                  Record Payment
                </button>
              </div>

              {/* Add Payment Form */}
              {showPayForm && (
                <div className="bg-gray-50 rounded-2xl p-4 mb-5 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Amount ({booking.currency})</label>
                      <input
                        type="number"
                        value={payAmount}
                        onChange={(e) => setPayAmount(e.target.value)}
                        placeholder="e.g. 5000"
                        className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 bg-white focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Payment Method</label>
                      <select
                        value={payMethod}
                        onChange={(e) => setPayMethod(e.target.value as PayMethod)}
                        className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 bg-white focus:outline-none cursor-pointer"
                      >
                        {payMethods.map((m) => <option key={m}>{m}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Reference</label>
                      <input
                        type="text"
                        value={payRef}
                        onChange={(e) => setPayRef(e.target.value)}
                        placeholder="e.g. TRF-0001"
                        className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 bg-white focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Notes</label>
                      <input
                        type="text"
                        value={payNote}
                        onChange={(e) => setPayNote(e.target.value)}
                        placeholder="Optional note"
                        className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 bg-white focus:outline-none"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={handleAddPayment} className="text-sm font-semibold px-4 py-2 rounded-xl text-white cursor-pointer" style={{ backgroundColor: brand }}>
                      Save Payment
                    </button>
                    <button onClick={() => setShowPayForm(false)} className="text-sm font-medium px-4 py-2 rounded-xl border border-gray-200 text-gray-500 cursor-pointer hover:bg-gray-50">
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Payment Progress Bar */}
              {booking.totalAmount > 0 && (
                <div className="mb-5">
                  <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                    <span>{Math.round((totalPaid / booking.totalAmount) * 100)}% collected</span>
                    <span>{booking.currency} {booking.totalAmount.toLocaleString()} total</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${Math.min((totalPaid / booking.totalAmount) * 100, 100)}%`, backgroundColor: brand }}
                    />
                  </div>
                </div>
              )}

              {/* Payment Records */}
              {payments.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No payments recorded yet.</p>
              ) : (
                <div className="space-y-2">
                  {payments.map((p) => (
                    <div key={p.id} className="flex items-center gap-4 bg-gray-50 rounded-xl px-4 py-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${brand}15` }}>
                        <i className="ri-money-dollar-circle-line text-sm" style={{ color: brand }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-bold text-gray-800">{booking.currency} {p.amount.toLocaleString()}</p>
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-600">
                            {p.method}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400">{p.date} · Ref: {p.reference}{p.notes ? ` · ${p.notes}` : ''}</p>
                      </div>
                      <span className="text-xs text-gray-400 flex-shrink-0">{p.recordedBy}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          {activeTab === 'notes' && (
            <div>
              <div className="bg-amber-50 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-sticky-note-line text-amber-500" />
                  <p className="text-sm font-semibold text-amber-700">Booking Notes</p>
                </div>
                <p className="text-sm text-amber-800 leading-relaxed">{booking.notes || 'No notes added.'}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center gap-3">
          <button
            className="flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl text-white cursor-pointer hover:opacity-90 whitespace-nowrap"
            style={{ backgroundColor: brand }}
          >
            <i className="ri-bill-line" />
            Generate Invoice
          </button>
          <button className="flex items-center gap-2 text-sm font-medium px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
            <i className="ri-coupon-3-line" />
            Create Voucher
          </button>
          <button className="flex items-center gap-2 text-sm font-medium px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
            <i className="ri-map-2-line" />
            Build Itinerary
          </button>
          <button className="ml-auto flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-600 cursor-pointer whitespace-nowrap">
            <i className="ri-delete-bin-line" />
            Cancel Booking
          </button>
        </div>
      </div>
    </div>
  );
}
